import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ImageUploader } from '../components/ImageUploader';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyCategory = {
  name_nl: '', name_en: '', name_fr: '',
  description_nl: '', description_en: '', description_fr: '',
  image_url: '',
  order: 0
};

export const AdminCategoriesPage = () => {
  const { getAuthHeader } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState(emptyCategory);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedCategory(null);
    setFormData(emptyCategory);
    setDialogOpen(true);
  };

  const handleEdit = (category) => {
    setSelectedCategory(category);
    setFormData({
      name_nl: category.name_nl || '',
      name_en: category.name_en || '',
      name_fr: category.name_fr || '',
      description_nl: category.description_nl || '',
      description_en: category.description_en || '',
      description_fr: category.description_fr || '',
      image_url: category.image_url || '',
      order: category.order || 0
    });
    setDialogOpen(true);
  };

  const handleDelete = (category) => {
    setSelectedCategory(category);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/admin/categories/${selectedCategory.id}`, {
        headers: getAuthHeader()
      });
      toast.success('Categorie verwijderd');
      fetchCategories();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (selectedCategory) {
        await axios.put(`${API}/admin/categories/${selectedCategory.id}`, formData, {
          headers: getAuthHeader()
        });
        toast.success('Categorie bijgewerkt');
      } else {
        await axios.post(`${API}/admin/categories`, formData, {
          headers: getAuthHeader()
        });
        toast.success('Categorie toegevoegd');
      }
      setDialogOpen(false);
      fetchCategories();
    } catch (error) {
      toast.error('Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#E31E24] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Categorieën</h1>
          <p className="text-gray-500 text-sm mt-1">{categories.length} categorieën</p>
        </div>
        <Button onClick={handleAdd} className="bg-[#E31E24] hover:bg-[#c91a1f] text-white">
          <Plus className="h-4 w-4 mr-2" />
          Nieuwe Categorie
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="w-20">Foto</TableHead>
              <TableHead>Naam</TableHead>
              <TableHead className="w-24">Volgorde</TableHead>
              <TableHead className="w-24 text-right">Acties</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {categories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-12 text-gray-500">
                  Geen categorieën gevonden. Voeg een nieuwe categorie toe.
                </TableCell>
              </TableRow>
            ) : (
              categories.map((category) => (
                <TableRow key={category.id} className="hover:bg-gray-50">
                  <TableCell>
                    {category.image_url ? (
                      <img 
                        src={category.image_url}
                        alt=""
                        className="w-12 h-12 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                        <span className="text-gray-400 text-xs">Geen</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="font-medium text-gray-900">{category.name_nl}</span>
                    {category.description_nl && (
                      <p className="text-gray-500 text-sm truncate max-w-xs">{category.description_nl}</p>
                    )}
                  </TableCell>
                  <TableCell className="text-gray-600">{category.order}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(category)}>
                      <Pencil className="h-4 w-4 text-gray-500" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(category)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {selectedCategory ? 'Categorie Bewerken' : 'Nieuwe Categorie'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            {/* Image Upload */}
            <ImageUploader 
              value={formData.image_url}
              onChange={(url) => handleChange('image_url', url)}
              label="Categorie Afbeelding"
            />

            {/* Order */}
            <div className="w-32">
              <Label className="text-gray-700">Volgorde</Label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => handleChange('order', parseInt(e.target.value) || 0)}
                className="mt-1.5"
              />
            </div>

            {/* Multilingual Content */}
            <Tabs defaultValue="nl" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="nl">Nederlands</TabsTrigger>
                <TabsTrigger value="en">English</TabsTrigger>
                <TabsTrigger value="fr">Français</TabsTrigger>
              </TabsList>
              
              <TabsContent value="nl" className="space-y-4 mt-4">
                <div>
                  <Label className="text-gray-700">Naam *</Label>
                  <Input
                    value={formData.name_nl}
                    onChange={(e) => handleChange('name_nl', e.target.value)}
                    required
                    placeholder="Categorienaam in Nederlands"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Beschrijving</Label>
                  <Textarea
                    value={formData.description_nl}
                    onChange={(e) => handleChange('description_nl', e.target.value)}
                    rows={3}
                    placeholder="Categoriebeschrijving in Nederlands"
                    className="mt-1.5"
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="en" className="space-y-4 mt-4">
                <div>
                  <Label className="text-gray-700">Name *</Label>
                  <Input
                    value={formData.name_en}
                    onChange={(e) => handleChange('name_en', e.target.value)}
                    required
                    placeholder="Category name in English"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Description</Label>
                  <Textarea
                    value={formData.description_en}
                    onChange={(e) => handleChange('description_en', e.target.value)}
                    rows={3}
                    placeholder="Category description in English"
                    className="mt-1.5"
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="fr" className="space-y-4 mt-4">
                <div>
                  <Label className="text-gray-700">Nom *</Label>
                  <Input
                    value={formData.name_fr}
                    onChange={(e) => handleChange('name_fr', e.target.value)}
                    required
                    placeholder="Nom de la catégorie en français"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Description</Label>
                  <Textarea
                    value={formData.description_fr}
                    onChange={(e) => handleChange('description_fr', e.target.value)}
                    rows={3}
                    placeholder="Description de la catégorie en français"
                    className="mt-1.5"
                  />
                </div>
              </TabsContent>
            </Tabs>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuleren
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                className="bg-[#E31E24] hover:bg-[#c91a1f] text-white"
              >
                {saving ? 'Opslaan...' : 'Opslaan'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Categorie Verwijderen</AlertDialogTitle>
            <AlertDialogDescription>
              Weet u zeker dat u "{selectedCategory?.name_nl}" wilt verwijderen? Alle producten in deze categorie worden ook verwijderd.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
